import { useSelector, shallowEqual } from "react-redux";
import moment from 'moment';
export function isAuthenticated() {
    const token = useSelector((store) => store?.auth?.userLogin?.response?.data?.accessToken, shallowEqual);
    return token !== undefined;
}
// Utility to format date strings to "HH:mm" using Moment.js
const formatTime = (dateStr) => dateStr.format("HH:mm");
export const transformData = (source) => {
    const groupedHours = {};
    Object.entries(source).forEach(([day, info]) => {
        if (!info.isOpen)
            return;
        // Determine open and close times
        const opens = info.is24Hours ? "00:00" : (info.timeRange ? formatTime(info.timeRange[0]) : "");
        const closes = info.is24Hours ? "23:59" : (info.timeRange ? formatTime(info.timeRange[1]) : "");
        // Skip if no valid open and close times
        if (!opens || !closes)
            return;
        const key = `${opens}-${closes}`;
        if (!groupedHours[key]) {
            groupedHours[key] = {
                opens,
                closes,
                dayOfWeek: []
            };
        }
        groupedHours[key].dayOfWeek.push(day);
    });
    return Object.values(groupedHours);
};
// Function to reverse the transformed data back to source format
// Function to reverse the transformed data back to source format
export const reverseTransformData = (transformedData) => {
    const originalSource = {};
    transformedData?.forEach(({ opens, closes, dayOfWeek }) => {
        const is24Hours = opens === "00:00" && closes === "23:59";
        let timeRange;
        // Construct the timeRange as a tuple of Moment objects if not 24 hours
        if (!is24Hours) {
            timeRange = [
                moment().startOf('day').hour(parseInt(opens.split(':')[0])).minute(parseInt(opens.split(':')[1])),
                moment().startOf('day').hour(parseInt(closes.split(':')[0])).minute(parseInt(closes.split(':')[1]))
            ]; // Casting to ensure it's treated as a tuple
        }
        dayOfWeek.forEach(day => {
            originalSource[day] = {
                isOpen: true,
                is24Hours,
                timeRange
            };
        });
    });
    // Fill in the rest of the days that are not specified in transformed data
    const allDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    allDays.forEach(day => {
        if (!originalSource[day]) {
            originalSource[day] = {
                isOpen: false,
                is24Hours: false,
                timeRange: undefined
            };
        }
    });
    return originalSource;
};
// Function to convert input color format to desired output format
export const convertColor = (input) => {
    const { metaColor } = input;
    if (!metaColor.isValid) {
        return null; // Return null if the color is not valid
    }
    const { r, g, b, a } = metaColor;
    // Convert to RGBA string
    const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;
    // Convert to HEX string
    const hex = `#${((1 << 24) + (r << 16) + (g << 8) + Math.round(a * 255))
        .toString(16)
        .slice(1)
        .toUpperCase()}`;
    // Return the output color format
    return {
        rgba,
        hex,
        opacity: a // Opacity from the alpha value
    };
};
const rgbToHsv = (r, g, b) => {
    // Normalize the RGB values to the range 0-1
    r /= 255;
    g /= 255;
    b /= 255;
    // Calculate max and min values from RGB
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, v = max;
    const d = max - min; // Difference between max and min
    // Calculate saturation
    s = max === 0 ? 0 : d / max;
    // Calculate hue
    if (max === min) {
        h = 0; // achromatic (no color)
    }
    else {
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break; // Red is max
            case g:
                h = (b - r) / d + 2;
                break; // Green is max
            case b:
                h = (r - g) / d + 4;
                break; // Blue is max
        }
        //@ts-ignore
        h /= 6; // Normalize to range [0, 1]
    }
    // Return the HSV values
    //@ts-ignore
    return { h: Math.round(h * 360), s, v }; // Hue in degrees, saturation and value in [0, 1]
};
export const reverseConvertColor = (output) => {
    // Extract rgba values from the string
    const rgbaMatch = output.rgba.match(/rgba\((\d+), (\d+), (\d+), ([\d\.]+)\)/);
    if (!rgbaMatch) {
        throw new Error("Invalid RGBA format");
    }
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    const a = parseFloat(rgbaMatch[4]);
    // Convert RGB to HSV
    const { h, s, v } = rgbToHsv(r, g, b);
    return {
        cleared: false,
        metaColor: {
            isValid: true,
            r,
            g,
            b,
            a,
            _h: h,
            _s: s,
            _v: v
        }
    };
};
export const arrayToHashMap = (array) => array.reduce((acc, { label, url }) => ({ ...acc, [label]: url }), {});
export const hashMapToArray = (hashMap) => Object.entries(hashMap).map(([label, url]) => ({ label, url }));
export const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = (error) => {
            reject(error);
        };
    });
};
export const convertToRadian = (angleInDegree) => {
    return -(angleInDegree * Math.PI) / 180;
};
